import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useKanban } from './KanbanContext';
import { useAuth } from './AuthContext';
import '../styles/TaskForm.css';
import { uploadFileToS3, logMessage } from '../utils/lambdaApi';
import { fetchUsersByBoardCode, fetchAdminUsers } from '../utils/api/permissionsApi';
import useEscapeKey from '../utils/useEscapeKey';
import { generateLocalTimestamp } from '../utils/taskUtils';
import { LABS_API_URL, DEFAULT_HEADERS, getEmailDomainMark } from '../utils/config';

// Function to get file icon based on file type
const getFileIcon = (file) => {
  const fileType = file.type?.toLowerCase() || '';
  const fileName = file.name?.toLowerCase() || '';
  
  // PDFs
  if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
    return '📄';
  }
  
  // Word documents
  if (fileType === 'application/msword' || 
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.doc') || 
      fileName.endsWith('.docx')) {
    return '📝';
  }
  
  // Excel
  if (fileType === 'application/vnd.ms-excel' ||
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileName.endsWith('.xls') ||
      fileName.endsWith('.xlsx')) {
    return '📊';
  }
  
  // Images
  if (fileType.startsWith('image/') || 
      fileName.endsWith('.jpg') || 
      fileName.endsWith('.jpeg') || 
      fileName.endsWith('.png') || 
      fileName.endsWith('.gif') || 
      fileName.endsWith('.bmp') || 
      fileName.endsWith('.webp')) {
    return '🖼️';
  }
  
  // Default file icon
  return '📎';
};

function TaskForm({ onComplete, onAddTask, onUpdateTask, taskToEdit }) {
  const { activeWorkspace, workspaces, addTag, getUserPermissions, userAccessibleWorkspaces, getAllAvailableWorkspaces, workspaceData } = useKanban();
  const { currentUser, userData } = useAuth();
  const [availableTags, setAvailableTags] = useState([]);
  const [targetWorkspace, setTargetWorkspace] = useState(''); // Inicializar como cadena vacía
  const [allWorkspaces, setAllWorkspaces] = useState([]); // Estado para todos los workspaces disponibles
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignee: '',
    status: 'todo', // Por defecto, siempre será "todo" (Pendiente)
    tags: [], // Campo para las etiquetas
    endDate: taskToEdit ? taskToEdit.endDate : '', // Sin fecha de vencimiento por defecto para nuevas tareas
    files: [], // Nuevo campo para almacenar múltiples archivos
    urls: [] // Nuevo campo para almacenar múltiples URLs
  });
  const [newTag, setNewTag] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [showNewTagInput, setShowNewTagInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Estado para controlar si se está enviando el formulario
  const [error, setError] = useState(null); // Estado para manejar errores

  // Add state for file upload
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Add state for file names at the beginning of the component
  const [uploadedFileNames, setUploadedFileNames] = useState([]);
  
  // Estados para el dropdown de usuarios
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersWithBoardAccess, setUsersWithBoardAccess] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  
  // Estados para el dropdown de usuarios con autocompletado
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [showAssigneeSuggestions, setShowAssigneeSuggestions] = useState(false);
  const [assigneeSuggestions, setAssigneeSuggestions] = useState([]);
  const assigneeInputRef = useRef(null);
  const assigneeSuggestionsRef = useRef(null);

  // Estados para el dropdown de etiquetas con autocompletado
  const [tagQuery, setTagQuery] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const tagInputRef = useRef(null);
  const tagSuggestionsRef = useRef(null);

  // State for handling multiple URLs
  const [newUrl, setNewUrl] = useState('');

  // Función para obtener el código de un workspace específico
  const getWorkspaceCode = useCallback((workspaceId) => {
    const workspace = allWorkspaces.find(ws => ws.id === workspaceId);
    
    if (!workspace) {
      return null;
    }
    
    // Mapeo manual para workspaces conocidos (temporal mientras se corrige la base de datos)
    const knownWorkspaceCodes = {
      'pprr': '11133',
      'xlabs-ia': '14773', // Tu código actual
      // Agregar más mapeos según sea necesario
    };
    
    // Verificar mapeo manual primero
    if (knownWorkspaceCodes[workspaceId]) {
      return knownWorkspaceCodes[workspaceId];
    }
    
    // Intentar obtener el código del workspace
    if (workspace.code && workspace.code.trim() !== '') {
      return workspace.code;
    }
    
    // Si no hay código del workspace, intentar extraerlo del departamento del usuario como fallback
    if (userData && userData.body && userData.body.department) {
      const departmentMatch = userData.body.department.match(/^(\d+)/);
      const fallbackCode = departmentMatch ? departmentMatch[1] : null;
      return fallbackCode;
    }
    
    return null;
  }, [allWorkspaces, userData]);

  // Formatear nombre de usuario desde email
  const formatUserName = (email) => {
    if (!email) return '';
    
    // Si no es un correo electrónico (no contiene @), devolver como está
    if (!email.includes('@')) return email;
    
    // Obtener la parte antes del @
    const namePart = email.split('@')[0];
    
    // Reemplazar puntos por espacios
    const nameWithSpaces = namePart.replace(/\./g, ' ');
    
    // Dividir el nombre completo en partes
    const nameParts = nameWithSpaces.split(' ');
    
    // Extraer el primer nombre y el primer apellido
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts[1] : '';
    
    // Capitalizar y combinar nombre y apellido
    const formattedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    const formattedLastName = lastName ? ' ' + lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase() : '';
    
    const mark = getEmailDomainMark(email);
    return formattedFirstName + formattedLastName + (mark ? ` ${mark}` : '');
  };

  // Cargar etiquetas disponibles del workspace actual
  useEffect(() => {
    if (activeWorkspace && workspaces[activeWorkspace]) {
      // Asegurar que todas las etiquetas estén limpias de comas
      const cleanedTags = (workspaces[activeWorkspace].availableTags || []).map(tag => {
        if (typeof tag === 'string') {
          // Convertir a mayúsculas y eliminar espacios en blanco
          const cleanTag = tag.trim().toUpperCase();
          // Eliminar comas
          return cleanTag.replace(/,/g, '');
        }
        return tag;
      }).filter(Boolean); // Eliminar valores vacíos
      
      setAvailableTags(cleanedTags);
    }
  }, [activeWorkspace, workspaces]);

  // Cargar workspaces disponibles según los permisos del usuario
  useEffect(() => {
    const loadWorkspaces = async () => {
      const userPermissions = getUserPermissions();
      
      if (userPermissions.sendTasks) {
        // Si el usuario tiene permisos sendTasks, cargar todos los workspaces disponibles
        try {
          const allAvailableWorkspaces = await getAllAvailableWorkspaces();
  
          setAllWorkspaces(allAvailableWorkspaces);
          logMessage(`Cargados ${allAvailableWorkspaces.length} workspaces para envío de tareas`);
        } catch (error) {
          console.error('Error al cargar todos los workspaces:', error);
          // Fallback a workspaces accesibles al usuario
          setAllWorkspaces(userAccessibleWorkspaces);
        }
      } else {
        // Si no tiene permisos sendTasks, usar solo los workspaces accesibles
        setAllWorkspaces(userAccessibleWorkspaces);
      }
    };

    // Inicializar con workspaces accesibles por defecto
    if (allWorkspaces.length === 0 && userAccessibleWorkspaces.length > 0) {
      setAllWorkspaces(userAccessibleWorkspaces);
    }

    // Solo cargar workspaces si no estamos editando una tarea existente
    if (!taskToEdit) {
      loadWorkspaces();
    }
  }, [getUserPermissions, userAccessibleWorkspaces, getAllAvailableWorkspaces, taskToEdit, allWorkspaces.length]);

  // Si se proporciona una tarea para editar, cargarla en el formulario
  useEffect(() => {
    if (taskToEdit) {
      // Asegurarnos de tener un array de URLs
      let urlsArray = [];
      
      // Si task.url es un array, usarlo directamente
      if (Array.isArray(taskToEdit.url)) {
        urlsArray = [...taskToEdit.url];
      } 
      // Si task.url es un string y no está vacío
      else if (taskToEdit.url && typeof taskToEdit.url === 'string') {
        // Verificar si es una URL o si es una lista de URLs concatenadas
        if (taskToEdit.url.includes('http')) {
          if (taskToEdit.url.includes('http', 8)) { // Si tiene más de una URL
            // Intentar separar las URLs
            const urlRegex = /(https?:\/\/[^\s]+)/g;
            const matches = taskToEdit.url.match(urlRegex);
            if (matches) {
              urlsArray = matches;
            } else {
              urlsArray = [taskToEdit.url];
            }
          } else {
            urlsArray = [taskToEdit.url];
          }
        }
      }
      
      // Obtener archivos de la tarea a editar
      const existingFiles = taskToEdit.files || [];
      
      // Cargar datos en el formulario
      setFormData({
        title: taskToEdit.title || '',
        description: taskToEdit.description || '',
        assignee: taskToEdit.assignee || '',
        status: taskToEdit.status || 'todo',
        tags: Array.isArray(taskToEdit.tags) ? taskToEdit.tags : [],
        endDate: taskToEdit.endDate || '',
        files: existingFiles,
        urls: urlsArray
      });
      
      // Inicializar el query de asignado si hay un usuario asignado
      if (taskToEdit.assignee) {
        setAssigneeQuery(formatUserName(taskToEdit.assignee));
      }
      
      // Establecer el workspace si está disponible
      if (taskToEdit.workspace) {
        setTargetWorkspace(taskToEdit.workspace);
      }
      
      // Si hay archivos en la tarea, cargarlos en el estado
      if (existingFiles.length > 0) {
        // Crear objetos para mostrar en la UI
        const pdfObjects = existingFiles.map(fileName => ({
          name: fileName,
          size: 0, // No tenemos el tamaño real, pero necesitamos una propiedad size
          type: 'application/pdf'
        }));
        
        setUploadedFiles(pdfObjects);
        setUploadedFileNames(existingFiles);
      }
    }
  }, [taskToEdit]);

  // Cargar los usuarios desde localStorage o API
  useEffect(() => {
    const fetchAllUsers = async () => {
      // Verificar si ya tenemos datos en localStorage
      const cachedUsers = localStorage.getItem('allUsersData');
      const cachedTimestamp = localStorage.getItem('allUsersTimestamp');
      const currentTime = new Date().getTime();
      
      // Usar caché si existe y tiene menos de 24 horas
      if (cachedUsers && cachedTimestamp && 
          (currentTime - parseInt(cachedTimestamp)) < 24 * 60 * 60 * 1000) {
        try {
          const users = JSON.parse(cachedUsers);
  
          setAllUsers(users);
          
          // Aplicar filtrado inmediatamente con la nueva lógica
          const workspaceToUse = targetWorkspace || activeWorkspace;
          if (workspaceToUse) {
            const workspaceCode = getWorkspaceCode(workspaceToUse);
            if (workspaceCode) {
              filterUsersByWorkspaceCode(users, workspaceCode);
            } else if (userData && userData.body && userData.body.department) {
              filterUsersByDepartment(users, userData.body.department);
            }
          }
          
          return;
        } catch (error) {
          console.error('Error al procesar la caché de usuarios:', error);
          // Si hay error en la caché, continuamos con la petición a la API
        }
      }
      
      setLoadingUsers(true);
      
              try {
          const response = await fetch(`${LABS_API_URL}/workspace/all-users`, {
          method: 'POST',
          headers: DEFAULT_HEADERS,
          body: JSON.stringify({})
        });
        
        if (!response.ok) {
          throw new Error(`Error al obtener usuarios: ${response.status}`);
        }
        
        const responseData = await response.json();
        
        // Manejar el formato de respuesta anidado
        let users = [];
        
        if (responseData && responseData.body) {
          try {
            // La respuesta tiene el body como string JSON, debemos parsearlo
            users = JSON.parse(responseData.body);
            
            if (Array.isArray(users)) {
              setAllUsers(users);
              
              // Guardar TODOS los usuarios en localStorage (sin filtrar)
              localStorage.setItem('allUsersData', JSON.stringify(users));
              localStorage.setItem('allUsersTimestamp', currentTime.toString());
              
              // Aplicar filtrado después de guardar
              const workspaceToUse = targetWorkspace || activeWorkspace;
              if (workspaceToUse) {
                const workspaceCode = getWorkspaceCode(workspaceToUse);
                if (workspaceCode) {
                  filterUsersByWorkspaceCode(users, workspaceCode);
                } else if (userData && userData.body && userData.body.department) {
                  filterUsersByDepartment(users, userData.body.department);
                }
              }
            }
          } catch (parseError) {
            console.error('Error al parsear el body:', parseError);
          }
        }
      } catch (err) {
        console.error('Error al obtener usuarios:', err);
      } finally {
        setLoadingUsers(false);
      }
    };
    
    if (currentUser) {
      fetchAllUsers();
    }
  }, [currentUser, targetWorkspace, activeWorkspace, getWorkspaceCode, userData]);

  // Manejar clics fuera del dropdown de asignado
  useEffect(() => {
    function handleClickOutside(event) {
      if (assigneeInputRef.current && !assigneeInputRef.current.contains(event.target) &&
          assigneeSuggestionsRef.current && !assigneeSuggestionsRef.current.contains(event.target)) {
        setShowAssigneeSuggestions(false);
      }
    }

    if (showAssigneeSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAssigneeSuggestions]);

  // Manejar clics fuera del dropdown de etiquetas
  useEffect(() => {
    function handleClickOutside(event) {
      if (tagInputRef.current && !tagInputRef.current.contains(event.target) &&
          tagSuggestionsRef.current && !tagSuggestionsRef.current.contains(event.target)) {
        setShowTagSuggestions(false);
      }
    }

    if (showTagSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showTagSuggestions]);

  // Filtrar usuarios según el workspace activo o el workspace seleccionado para envío
  useEffect(() => {
    if (allUsers.length > 0) {
      // Determinar qué workspace usar para el filtrado
      const workspaceToUse = targetWorkspace || activeWorkspace;
      
      if (workspaceToUse) {
        // Obtener el código del workspace
        const workspaceCode = getWorkspaceCode(workspaceToUse);
        if (workspaceCode) {
          filterUsersByWorkspaceCode(allUsers, workspaceCode);
        } else {
          // Si no encontramos el código del workspace, usar filtrado por departamento como fallback
          if (userData && userData.body && userData.body.department) {
            filterUsersByDepartment(allUsers, userData.body.department);
          }
        }
      } else {
        // Si no hay workspace, usar filtrado por departamento
        if (userData && userData.body && userData.body.department) {
          filterUsersByDepartment(allUsers, userData.body.department);
        }
      }
    }
  }, [targetWorkspace, activeWorkspace, allUsers, userData, getWorkspaceCode]);

  // Obtener usuarios con acceso especial al tablero (por additionalCodes)
  useEffect(() => {
    const fetchUsersWithBoardAccess = async () => {
      const workspaceToUse = targetWorkspace || activeWorkspace;
      if (!workspaceToUse) return;
      
      const workspaceCode = getWorkspaceCode(workspaceToUse);
      if (!workspaceCode) return;
      
      try {
        const boardUsers = await fetchUsersByBoardCode(workspaceCode);
        
        if (Array.isArray(boardUsers)) {
          setUsersWithBoardAccess(boardUsers);
        }
      } catch (error) {
        console.error('Error al obtener usuarios con acceso al tablero:', error);
      }
    };
    
    fetchUsersWithBoardAccess();
  }, [targetWorkspace, activeWorkspace, workspaceData, getWorkspaceCode]);

  // Obtener usuarios administradores
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const adminUserEmails = await fetchAdminUsers();
        setAdminUsers(adminUserEmails);
      } catch (error) {
        console.error('Error fetching admin users:', error);
        setAdminUsers([]);
      }
    };

    if (allUsers.length > 0) {
      fetchAdmins();
    }
  }, [allUsers]);

  // Combinar usuarios regulares con usuarios con acceso especial y administradores
  useEffect(() => {
    // Crear objetos de usuario para los administradores que no están en filteredUsers
    const adminUsersList = adminUsers
      .filter(email => !filteredUsers.some(u => u.email?.toLowerCase() === email?.toLowerCase()))
      .map(email => {
        // Buscar el usuario completo en allUsers si existe (comparación case-insensitive)
        const fullUser = allUsers.find(u => u.email?.toLowerCase() === email?.toLowerCase());
        const hasAccess = usersWithBoardAccess.some(u => u?.toLowerCase() === email?.toLowerCase());
        if (fullUser) {
          return {
            ...fullUser,
            isAdmin: true,
            hasSpecialAccess: hasAccess
          };
        }
        // Si no está en allUsers, crear un objeto básico
        return {
          email: email,
          department: 'Administrador', // Indicador visual
          isAdmin: true,
          hasSpecialAccess: hasAccess
        };
      });
    
    // Crear objetos de usuario para los que tienen acceso especial pero no están en filteredUsers ni son admin
    const additionalUsers = usersWithBoardAccess
      .filter(email => !filteredUsers.some(u => u.email === email) && !adminUsers.includes(email))
      .map(email => ({
        email: email,
        department: 'Acceso especial',
        hasSpecialAccess: true,
        isAdmin: false
      }));
    
    // Combinar usuarios regulares con usuarios con acceso especial y administradores
    const combinedUsers = [
      ...filteredUsers.map(user => ({
        ...user,
        hasSpecialAccess: usersWithBoardAccess.some(u => u?.toLowerCase() === user.email?.toLowerCase()),
        isAdmin: adminUsers.some(a => a?.toLowerCase() === user.email?.toLowerCase())
      })),
      ...adminUsersList,
      ...additionalUsers
    ];
    
    // Ordenar: administradores primero, luego el resto
    combinedUsers.sort((a, b) => {
      if (a.isAdmin && !b.isAdmin) return -1;
      if (!a.isAdmin && b.isAdmin) return 1;
      return 0;
    });
    
    // Actualizar las sugerencias si están visibles
    if (showAssigneeSuggestions) {
      if (assigneeQuery.trim() === '') {
        setAssigneeSuggestions(combinedUsers);
      } else {
        const filtered = combinedUsers.filter(user => {
          const formattedName = formatUserName(user.email);
          return formattedName.toLowerCase().includes(assigneeQuery.toLowerCase()) ||
                 user.email.toLowerCase().includes(assigneeQuery.toLowerCase());
        });
        setAssigneeSuggestions(filtered);
      }
    }
  }, [filteredUsers, usersWithBoardAccess, adminUsers, assigneeQuery, showAssigneeSuggestions, allUsers]);
  
  // Función para filtrar usuarios por departamento
  const filterUsersByDepartment = (users, department) => {
    if (!department) return;
    
    // Extraer el código de departamento del usuario logueado (ej: 14337 de "14337 - ADM - GGL - XLABS IA")
    const departmentMatch = department.match(/^(\d+)/);
    const loggedUserCode = departmentMatch ? departmentMatch[1] : '';
    
    if (!loggedUserCode) {
      setFilteredUsers(users);
      return;
    }
    
    // Filtrar usuarios que tengan el mismo código de departamento
    const filtered = users.filter(user => {
      // Verificar que user.department existe y contiene el formato esperado
      if (!user.department) return false;
      
      const userDeptMatch = user.department.match(/^(\d+)/);
      const userDeptCode = userDeptMatch ? userDeptMatch[1] : '';
      return userDeptCode === loggedUserCode;
    });
    

    
    setFilteredUsers(filtered);
  };

  // Nueva función para filtrar usuarios por código de workspace
  const filterUsersByWorkspaceCode = (users, workspaceCode) => {
    if (!workspaceCode) {
      setFilteredUsers(users);
      return;
    }
    
    // Filtrar usuarios que pertenezcan al workspace (cuyo código de departamento coincida con el código del workspace)
    const filtered = users.filter(user => {
      if (!user.department) return false;
      
      // El campo department puede tener diferentes formatos:
      // 1. Solo el código: "14337"
      // 2. Formato completo: "14337 - ADM - GGL - XLABS IA"
      
      // Primero intentar comparación directa (caso 1)
      if (user.department === workspaceCode || user.department?.toString() === workspaceCode?.toString()) {
        return true;
      }
      
      // Si no coincide directamente, extraer el código del formato completo (caso 2)
      const userDeptMatch = user.department.match(/^(\d+)/);
      const userDeptCode = userDeptMatch ? userDeptMatch[1] : '';
      return userDeptCode === workspaceCode;
    });
    
    setFilteredUsers(filtered);
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Manejar cambios en el input de asignado
  const handleAssigneeQueryChange = async (e) => {
    const query = e.target.value;
    setAssigneeQuery(query);
    
    // Siempre mostrar sugerencias
    setShowAssigneeSuggestions(true);
    
    // Asegurarse de que tenemos los usuarios con acceso especial
    const workspaceToUse = targetWorkspace || activeWorkspace;
    let currentUsersWithBoardAccess = usersWithBoardAccess;
    
    // Si no tenemos usuarios con acceso especial, intentar cargarlos
    if (currentUsersWithBoardAccess.length === 0 && workspaceToUse) {
      const workspaceCode = getWorkspaceCode(workspaceToUse);
      if (workspaceCode) {
        try {
          const boardUsers = await fetchUsersByBoardCode(workspaceCode);
          if (Array.isArray(boardUsers)) {
            currentUsersWithBoardAccess = boardUsers;
            setUsersWithBoardAccess(boardUsers);
          }
        } catch (error) {
          console.error('Error loading users with board access:', error);
        }
      }
    }
    
    // Crear objetos de usuario para los administradores que no están en filteredUsers
    const adminUsersList = adminUsers
      .filter(email => !filteredUsers.some(u => u.email?.toLowerCase() === email?.toLowerCase()))
      .map(email => {
        // Buscar el usuario completo en allUsers si existe (comparación case-insensitive)
        const fullUser = allUsers.find(u => u.email?.toLowerCase() === email?.toLowerCase());
        const hasAccess = currentUsersWithBoardAccess.some(u => u?.toLowerCase() === email?.toLowerCase());
        if (fullUser) {
          return {
            ...fullUser,
            isAdmin: true,
            hasSpecialAccess: hasAccess
          };
        }
        // Si no está en allUsers, crear un objeto básico
        return {
          email: email,
          department: 'Administrador', // Indicador visual
          isAdmin: true,
          hasSpecialAccess: hasAccess
        };
      });
    
    // Crear objetos de usuario para los que tienen acceso especial pero no están en filteredUsers ni son admin
    const additionalUsers = currentUsersWithBoardAccess
      .filter(email => !filteredUsers.some(u => u.email === email) && !adminUsers.includes(email))
      .map(email => ({
        email: email,
        department: 'Acceso especial',
        hasSpecialAccess: true,
        isAdmin: false
      }));
    
    // Combinar usuarios regulares con usuarios con acceso especial y administradores
    const combinedUsers = [
      ...filteredUsers.map(user => ({
        ...user,
        hasSpecialAccess: currentUsersWithBoardAccess.some(u => u?.toLowerCase() === user.email?.toLowerCase()),
        isAdmin: adminUsers.some(a => a?.toLowerCase() === user.email?.toLowerCase())
      })),
      ...adminUsersList,
      ...additionalUsers
    ];
    
    // Filtrar usuarios basado en la consulta
    if (query.trim() === '') {
      // Si está vacío, mostrar todos los usuarios disponibles
      setAssigneeSuggestions(combinedUsers);
      // Limpiar la asignación si se borra el texto
      setFormData(prev => ({ ...prev, assignee: '' }));
    } else {
      const filtered = combinedUsers.filter(user => {
        const formattedName = formatUserName(user.email);
        return formattedName.toLowerCase().includes(query.toLowerCase()) ||
               user.email.toLowerCase().includes(query.toLowerCase());
      });
      setAssigneeSuggestions(filtered);
    }
  };

  // Manejar focus en el input de asignado
  const handleAssigneeFocus = async () => {
    // Primero mostrar las sugerencias
    setShowAssigneeSuggestions(true);
    
    // Asegurarse de que tenemos los usuarios con acceso especial
    const workspaceToUse = targetWorkspace || activeWorkspace;
    let currentUsersWithBoardAccess = usersWithBoardAccess;
    
    // Si no tenemos usuarios con acceso especial, intentar cargarlos
    if (currentUsersWithBoardAccess.length === 0 && workspaceToUse) {
      const workspaceCode = getWorkspaceCode(workspaceToUse);
      if (workspaceCode) {
        try {
          const boardUsers = await fetchUsersByBoardCode(workspaceCode);
          if (Array.isArray(boardUsers)) {
            currentUsersWithBoardAccess = boardUsers;
            setUsersWithBoardAccess(boardUsers);
          }
        } catch (error) {
          console.error('Error loading users with board access:', error);
        }
      }
    }
    
    // Crear objetos de usuario para los administradores que no están en filteredUsers
    const adminUsersList = adminUsers
      .filter(email => !filteredUsers.some(u => u.email?.toLowerCase() === email?.toLowerCase()))
      .map(email => {
        // Buscar el usuario completo en allUsers si existe (comparación case-insensitive)
        const fullUser = allUsers.find(u => u.email?.toLowerCase() === email?.toLowerCase());
        const hasAccess = currentUsersWithBoardAccess.some(u => u?.toLowerCase() === email?.toLowerCase());
        if (fullUser) {
          return {
            ...fullUser,
            isAdmin: true,
            hasSpecialAccess: hasAccess
          };
        }
        // Si no está en allUsers, crear un objeto básico
        return {
          email: email,
          department: 'Administrador', // Indicador visual
          isAdmin: true,
          hasSpecialAccess: hasAccess
        };
      });
    
    // Crear objetos de usuario para los que tienen acceso especial pero no están en filteredUsers ni son admin
    const additionalUsers = currentUsersWithBoardAccess
      .filter(email => !filteredUsers.some(u => u.email === email) && !adminUsers.includes(email))
      .map(email => ({
        email: email,
        department: 'Acceso especial',
        hasSpecialAccess: true,
        isAdmin: false
      }));
    
    // Combinar usuarios regulares con usuarios con acceso especial y administradores
    const combinedUsers = [
      ...filteredUsers.map(user => ({
        ...user,
        hasSpecialAccess: currentUsersWithBoardAccess.some(u => u?.toLowerCase() === user.email?.toLowerCase()),
        isAdmin: adminUsers.some(a => a?.toLowerCase() === user.email?.toLowerCase())
      })),
      ...adminUsersList,
      ...additionalUsers
    ];
    
    // Ordenar: administradores primero, luego el resto
    combinedUsers.sort((a, b) => {
      if (a.isAdmin && !b.isAdmin) return -1;
      if (!a.isAdmin && b.isAdmin) return 1;
      return 0;
    });
    
    if (assigneeQuery.trim() === '') {
      setAssigneeSuggestions(combinedUsers);
    } else {
      const filtered = combinedUsers.filter(user => {
        const formattedName = formatUserName(user.email);
        return formattedName.toLowerCase().includes(assigneeQuery.toLowerCase()) ||
               user.email.toLowerCase().includes(assigneeQuery.toLowerCase());
      });
      setAssigneeSuggestions(filtered);
    }
  };

  // Manejar selección de usuario
  const handleSelectAssignee = (user) => {
    const formattedName = formatUserName(user.email);
    setAssigneeQuery(formattedName);
    setFormData(prev => ({ ...prev, assignee: user.email }));
    setShowAssigneeSuggestions(false);
  };

  // Limpiar filtro de asignado
  const clearAssigneeFilter = () => {
    setAssigneeQuery('');
    setFormData(prev => ({ ...prev, assignee: '' }));
    setShowAssigneeSuggestions(false);
  };

  // Manejar cambios en el input de etiquetas
  const handleTagQueryChange = (e) => {
    const query = e.target.value;
    setTagQuery(query);
    
    // Siempre mostrar sugerencias
    setShowTagSuggestions(true);
    
    // Filtrar etiquetas basado en la consulta
    if (query.trim() === '') {
      // Si está vacío, mostrar todas las etiquetas disponibles (excluyendo las ya seleccionadas)
      const availableTagsFiltered = availableTags.filter(tag => !formData.tags.includes(tag));
      setTagSuggestions(availableTagsFiltered);
    } else {
      const filtered = availableTags.filter(tag => {
        return tag.toLowerCase().includes(query.toLowerCase()) &&
               !formData.tags.includes(tag); // Excluir etiquetas ya seleccionadas
      });
      setTagSuggestions(filtered);
    }
  };

  // Manejar focus en el input de etiquetas
  const handleTagFocus = () => {
    setShowTagSuggestions(true);
    // Si no hay query, mostrar todas las etiquetas disponibles
    if (tagQuery.trim() === '') {
      const availableTagsFiltered = availableTags.filter(tag => !formData.tags.includes(tag));
      setTagSuggestions(availableTagsFiltered);
    }
  };

  // Manejar selección de etiqueta
  const handleSelectTag = (tag) => {
    // Añadir la etiqueta a la lista de etiquetas seleccionadas
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, tag]
    }));
    // Limpiar el input
    setTagQuery('');
    setShowTagSuggestions(false);
  };

  // Limpiar input de etiquetas
  const clearTagQuery = () => {
    setTagQuery('');
    setShowTagSuggestions(false);
  };

  // Manejar tecla Enter para agregar etiqueta personalizada
  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (tagQuery.trim() && !formData.tags.includes(tagQuery.trim().toUpperCase())) {
        const newTag = tagQuery.trim().toUpperCase();
        // Añadir la etiqueta a la lista de etiquetas disponibles en el workspace
        addTag(activeWorkspace, newTag);
        // Añadir la etiqueta a la tarea actual
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
        // Limpiar el input
        setTagQuery('');
        setShowTagSuggestions(false);
      }
    }
  };

  // Manejar eliminación de una etiqueta
  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Manejar la creación de una nueva etiqueta
  // eslint-disable-next-line no-unused-vars
  const handleAddNewTag = () => {
    if (newTag.trim() !== '') {
      const upperCaseTag = newTag.trim().toUpperCase();
      // Añadir la etiqueta a la lista de etiquetas disponibles en el workspace
      addTag(activeWorkspace, upperCaseTag);
      
      // Añadir la etiqueta a la tarea actual
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, upperCaseTag]
      }));
      
      // Limpiar y ocultar el input
      setNewTag('');
      setShowNewTagInput(false);
    }
  };

  // Handle file selection
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    
    // Filtrar para archivos permitidos: PDFs, Word, e imágenes
    const allowedFiles = files.filter(file => {
      const fileType = file.type.toLowerCase();
      const fileName = file.name.toLowerCase();
      
      // PDFs
      if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
        return true;
      }
      
      // Documentos Word
      if (fileType === 'application/msword' || 
          fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          fileName.endsWith('.doc') || 
          fileName.endsWith('.docx')) {
        return true;
      }
      
      // Excel
      if (fileType === 'application/vnd.ms-excel' ||
          fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          fileName.endsWith('.xls') ||
          fileName.endsWith('.xlsx')) {
        return true;
      }
      
      // Imágenes
      if (fileType.startsWith('image/') || 
          fileName.endsWith('.jpg') || 
          fileName.endsWith('.jpeg') || 
          fileName.endsWith('.png') || 
          fileName.endsWith('.gif') || 
          fileName.endsWith('.bmp') || 
          fileName.endsWith('.webp')) {
        return true;
      }
      
      return false;
    });
    
    if (allowedFiles.length === 0) {
      alert('Solo se permiten archivos PDF, Word (.doc, .docx), Excel (.xls, .xlsx) e imágenes (.jpg, .jpeg, .png, .gif, .bmp, .webp).');
      return;
    }
    
    // Actualizar estado para mostrar archivos seleccionados
    setSelectedFiles(allowedFiles);
    
    // Iniciar proceso de carga
    setIsUploading(true);
    setUploadProgress(0);
    
    const newUploadedFiles = [];
    const newUploadedFileNames = []; // Guardar nombres de archivo
    
    // Procesar cada archivo
    for (let i = 0; i < allowedFiles.length; i++) {
      const file = allowedFiles[i];
      
      try {
        setUploadProgress(Math.round((i / allowedFiles.length) * 50));
        
        // Llamar a la API para subir el archivo a S3
        const result = await uploadFileToS3(file);
        
        let url = '';
        let fileRef = file.name; // referencia para backend/descarga (key cuando es presign)
        if (result.body && typeof result.body === 'string') {
          try {
            const parsedBody = JSON.parse(result.body);
            url = parsedBody.url || '';
            if (parsedBody.key) fileRef = parsedBody.key;
          } catch (e) {
            console.error('Error al analizar respuesta de API:', e);
          }
        }
        newUploadedFileNames.push(fileRef);
        newUploadedFiles.push({
          name: fileRef,
          size: file.size,
          type: file.type,
          url
        });
      } catch (error) {
        console.error(`Error al subir el archivo ${file.name}:`, error);
      }
    }
    
    // Verificar si alguno de los archivos nuevos ya existe para evitar duplicados
    const existingFileNames = new Set(uploadedFileNames);
    const uniqueNewFiles = newUploadedFiles.filter(file => !existingFileNames.has(file.name));
    const uniqueNewFileNames = newUploadedFileNames.filter(name => !existingFileNames.has(name));

    // Actualizar estado con los archivos únicos subidos
    if (uniqueNewFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...uniqueNewFiles]);
      setUploadedFileNames(prev => [...prev, ...uniqueNewFileNames]);
      
      // Si estamos editando, también actualizamos el estado formData
      if (taskToEdit) {
        setFormData(prev => ({
          ...prev,
          files: [...prev.files, ...uniqueNewFileNames]
        }));
      }
    }
    setUploadProgress(100);
    
    // Reset UI
    setTimeout(() => {
      setIsUploading(false);
      setSelectedFiles([]);
    }, 1000);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeUploadedFile = (index) => {
    // Remover el archivo de uploadedFiles
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    
    // Remover el archivo de uploadedFileNames y guardar el nombre eliminado
    let removedFileName = '';
    setUploadedFileNames(prev => {
      // Guardar el nombre del archivo que se está eliminando
      if (index >= 0 && index < prev.length) {
        removedFileName = prev[index];
      }
      return prev.filter((_, i) => i !== index);
    });
    
    // Si estamos editando y el archivo eliminado existe en formData.files, también eliminarlo de allí
    if (taskToEdit && removedFileName) {
      setFormData(prev => ({
        ...prev,
        files: prev.files.filter(fileName => fileName !== removedFileName)
      }));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const formatDescription = (description) => {
    if (!description) return '';
    
    // Detectar enlaces y formatearlos como hipervínculos
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    return description.replace(urlRegex, (url) => {
      // Intentar limpiar la URL de caracteres de puntuación al final
      const cleanUrl = url.replace(/[.,;:!?]$/, '');
      return `<a href="${cleanUrl}" target="_blank">${cleanUrl}</a>`;
    });
  };

  // Function to add a new URL to the list
  const handleAddUrl = () => {
    if (!newUrl) return;
    
    // Basic URL validation
    try {
      new URL(newUrl); // This will throw if the URL is invalid
      
      // Add URL to the form data
      setFormData(prev => ({
        ...prev,
        urls: [...prev.urls, newUrl]
      }));
      
      // Clear the input
      setNewUrl('');
    } catch (error) {
      alert('Por favor, ingrese una URL válida');
    }
  };
  
  // Function to remove a URL from the list
  const handleRemoveUrl = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      urls: prev.urls.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Si hay una URL en el input, intentar validarla y añadirla
      let allUrls = [...formData.urls];
      if (newUrl.trim()) {
        try {
          new URL(newUrl); // Validar que es una URL válida
          allUrls.push(newUrl); // Añadirla al array de URLs
        } catch (error) {
          // URL no válida, se omite
        }
      }
      
      // Formatear la fecha de vencimiento al formato deseado: "HH:MM DD-MM-YYYY"
      let formattedEndDate = '';
      if (formData.endDate) {
        try {
          // Si estamos editando una tarea y la fecha no ha cambiado, usar la fecha original
          if (taskToEdit && formData.endDate === taskToEdit.endDate) {
            formattedEndDate = taskToEdit.endDate;

          } else {
            // Crear un objeto Date a partir del input datetime-local (que está en hora local)
            const endDate = new Date(formData.endDate);
            
            if (!isNaN(endDate.getTime())) {
              // Formatear la hora - siempre a las 17:00 exactamente
              const hours = "17";
              const minutes = "00";
              
              // Formatear la fecha - mantener día, mes y año
              const day = endDate.getDate().toString().padStart(2, '0');
              const month = (endDate.getMonth() + 1).toString().padStart(2, '0');
              const year = endDate.getFullYear();
              
              // Usar el formato con UN solo espacio entre hora y fecha
              formattedEndDate = `${hours}:${minutes} ${day}-${month}-${year}`;
              
              
            } else {
              formattedEndDate = formData.endDate; // Mantener como está si no es válida
              
            }
          }
        } catch (error) {
          console.error('Error al formatear fecha de vencimiento:', error);
          formattedEndDate = formData.endDate;
        }
      }
      
      // Ensure minimal task values are set
      const finalTask = {
        ...formData,
        status: formData.status || 'todo',
        createdBy: currentUser?.email || 'Usuario',
        createdTimestamp: generateLocalTimestamp(), // Usar timestamp local en lugar de ISO
        tags: formData.tags || [],
        workspace: targetWorkspace || activeWorkspace,
        endDate: formattedEndDate // Usar la fecha formateada
      };
      
      // Manejar archivos correctamente para evitar duplicaciones
      if (taskToEdit) {
        // Si estamos editando una tarea existente, usamos solo los archivos que están en uploadedFileNames
        // ya que este array contiene tanto los archivos que ya existían como los nuevos que se subieron
        finalTask.files = uploadedFileNames;
      } else {
        // Para tareas nuevas, combinamos los archivos del formData con los recién subidos
        finalTask.files = [...(formData.files || []), ...uploadedFileNames];
      }
      
      // Usar el array de URLs directamente en el campo url
      finalTask.url = allUrls.length > 0 ? allUrls : '';
      
      // Para depuración, mostrar las URLs que se van a guardar
      
      
      // If we're editing an existing task, don't overwrite creation fields
      if (taskToEdit) {
        delete finalTask.createdBy;
        delete finalTask.createdTimestamp;
        
        
        // Inicializar el historial de actividades si no existe
        if (!finalTask.activityHistory) {
          finalTask.activityHistory = taskToEdit.activityHistory || [];
        }
        
        const timestamp = generateLocalTimestamp(); // Usar timestamp local
        const userEmail = currentUser?.email || 'Usuario';
        const activityEntries = [];
        
        // Registrar cambios en el título
        if (finalTask.title !== taskToEdit.title) {
          activityEntries.push({
            timestamp,
            type: "EDIT",
            field: "title",
            oldValue: taskToEdit.title || '',
            newValue: finalTask.title || '',
            updatedBy: userEmail
          });
        }
        
        // Registrar cambios en la descripción
        if (finalTask.description !== taskToEdit.description) {
          activityEntries.push({
            timestamp,
            type: "EDIT",
            field: "description",
            oldValue: taskToEdit.description || '',
            newValue: finalTask.description || '',
            updatedBy: userEmail
          });
        }
        
        // Registrar cambios en el estado
        if (finalTask.status !== taskToEdit.status) {
          activityEntries.push({
            timestamp,
            type: "STATUS_CHANGE",
            field: "status",
            oldValue: taskToEdit.status || '',
            newValue: finalTask.status || '',
            updatedBy: userEmail
          });
        }
        
        // Registrar cambios en la asignación
        if (finalTask.assignee !== taskToEdit.assignee) {
          activityEntries.push({
            timestamp,
            type: "ASSIGNEE_CHANGE",
            field: "assignee",
            oldValue: taskToEdit.assignee || '',
            newValue: finalTask.assignee || '',
            updatedBy: userEmail
          });
        }
        
        // Registrar cambios en la fecha de vencimiento
        const oldEndDate = taskToEdit.endDate || '';
        const newEndDate = finalTask.endDate || '';
        
        // Solo registrar si las fechas son diferentes
        let fechaCambió = oldEndDate !== newEndDate;
        
        // Si las fechas parecen diferentes, verificar si realmente cambiaron ignorando el formato
        if (fechaCambió && oldEndDate && newEndDate) {
          // Comparar las partes de fecha ignorando el formato
          const extractFechaHora = (fechaString) => {
            if (!fechaString || typeof fechaString !== 'string') return '';
            
            // Buscar patrón DD-MM-YYYY al final de la cadena
            const datePattern = /(\d{2}-\d{2}-\d{4})$/;
            const match = fechaString.match(datePattern);
            return match ? match[1] : fechaString;
          };
          
          const soloFechaAnterior = extractFechaHora(oldEndDate);
          const soloFechaNueva = extractFechaHora(newEndDate);
          
          // Si la parte de fecha es igual, la fecha no cambió realmente
          if (soloFechaAnterior === soloFechaNueva) {
            fechaCambió = false;
          }
        }
        
        // Solo registrar si realmente cambió la fecha
        if (fechaCambió) {
          activityEntries.push({
            timestamp,
            type: "DEADLINE_CHANGE",
            field: "deadline",
            oldValue: oldEndDate,
            newValue: newEndDate,
            updatedBy: userEmail
          });
        }
        
        // Registrar cambios en las etiquetas
        const oldTags = Array.isArray(taskToEdit.tags) ? 
          taskToEdit.tags.join(', ') : 
          (taskToEdit.tags || '');
        const newTags = Array.isArray(finalTask.tags) ? 
          finalTask.tags.join(', ') : 
          (finalTask.tags || '');
        
        if (newTags !== oldTags) {
          activityEntries.push({
            timestamp,
            type: "EDIT",
            field: "tags",
            oldValue: oldTags,
            newValue: newTags,
            updatedBy: userEmail
          });
        }
        
        // Registrar la actividad de agregar URLs si se han añadido nuevas
        if (allUrls.length > 0 && (!taskToEdit.url || 
            (Array.isArray(taskToEdit.url) && allUrls.length > taskToEdit.url.length) ||
            (!Array.isArray(taskToEdit.url) && allUrls.length > 0))) {
          
          // Determinar las URLs nuevas
          const oldUrls = Array.isArray(taskToEdit.url) ? taskToEdit.url : 
                        (taskToEdit.url ? [taskToEdit.url] : []);
          const newUrls = allUrls.filter(url => !oldUrls.includes(url));
          
          if (newUrls.length > 0) {
            // Crear entrada para el historial de actividad
            activityEntries.push({
              timestamp,
              type: "URL_ADDED",
              field: "url",
              oldValue: oldUrls.join(', '),
              newValue: allUrls.join(', '),
              updatedBy: userEmail
            });
          }
        }
        
        // Agregar todas las entradas de actividad al historial
        if (activityEntries.length > 0) {

          finalTask.activityHistory = [...finalTask.activityHistory, ...activityEntries];
        }
      }
      
      // Initialize unified activity history for new tasks
      if (!taskToEdit) {
        // Inicializar el historial de actividad unificado
        finalTask.activityHistory = [];
        
        // Añadir solo la actividad de creación, no añadir entradas para valores por defecto
        finalTask.activityHistory.push({
          timestamp: finalTask.createdTimestamp,
          type: "CREATION",
          field: "task",
          oldValue: null,
          newValue: finalTask.title,
          updatedBy: finalTask.createdBy
        });
        
        // No registrar estos eventos cuando son valores por defecto
        // Solo registrar la asignación inicial si fue explícitamente seleccionada
        if (finalTask.assignee) {
          finalTask.activityHistory.push({
            timestamp: finalTask.createdTimestamp,
            type: "ASSIGNEE_CHANGE",
            field: "assignee",
            oldValue: null,
            newValue: finalTask.assignee,
            updatedBy: finalTask.createdBy
          });
        }
        
        // Registrar archivos iniciales si se añadieron al crear la tarea
        if (finalTask.files && finalTask.files.length > 0) {
          finalTask.activityHistory.push({
            timestamp: finalTask.createdTimestamp,
            type: "ATTACHMENT",
            field: "files",
            oldValue: "",
            newValue: finalTask.files.join(', '),
            updatedBy: finalTask.createdBy
          });
        }
        
        // Registrar URLs iniciales si se añadieron al crear la tarea
        if (finalTask.url && finalTask.url.length > 0) {
          const urlsToRecord = Array.isArray(finalTask.url) ? finalTask.url : [finalTask.url];
          if (urlsToRecord.some(url => url && url.trim())) {
            finalTask.activityHistory.push({
              timestamp: finalTask.createdTimestamp,
              type: "URL_ADDED", 
              field: "url",
              oldValue: "",
              newValue: urlsToRecord.filter(url => url && url.trim()).join(', '),
              updatedBy: finalTask.createdBy
            });
          }
        }
        
        // Ya no registramos automáticamente estos eventos cuando son valores por defecto
        // Sólo los registramos cuando se edita una tarea existente o cuando se añaden explícitamente
      }
      
      // Process and submit the task
      if (taskToEdit) {
        // Update existing task
        if (onUpdateTask) {
          // Convert files array if necessary
          onUpdateTask(finalTask);
        }
      } else {
        // Create new task
        if (onAddTask) {
          onAddTask(finalTask);
        }
      }
      
      // Clear form if not editing
      if (!taskToEdit) {
        setFormData({
          title: '',
          description: '',
          status: 'todo',
          tags: [],
          assignee: '',
          endDate: '',
          files: [],
          urls: []
        });
        setUploadedFiles([]);
        setUploadedFileNames([]);
        setNewUrl(''); // Limpiar también el campo de nueva URL
      }
      
      // Call onComplete to close modal only if we're not editing
      // When editing, keep the modal open to continue working
      if (onComplete && !taskToEdit) {
        onComplete();
      }
    } catch (error) {
      console.error('Error saving task:', error);
      setError(`Error al guardar: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cerrar formulario con la tecla Escape
  useEscapeKey(() => {
    if (onComplete && !isSubmitting) {
      onComplete();
    }
  });

  return (
    <form onSubmit={handleSubmit} className="task-form custom-form">
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      <div className="form-group">
        <label htmlFor="title">Título</label>
        <input
          id="title"
          name="title"
          type="text"
          value={formData.title}
          onChange={handleChange}
          placeholder="Título de la tarea"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Descripción</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Descripción detallada de la tarea"
          rows="6"
          className="description-textarea"
        />
      </div>

      {/* Selector de tablero de destino como switch - solo si el usuario tiene permisos sendTasks */}
      {!taskToEdit && getUserPermissions().sendTasks && (
        <div className="form-group send-to-board-container">
          <div className="send-board-switch-container">
            <label htmlFor="sendToBoard" className="switch-text">
              <span className="switch-icon">📋</span> Enviar a tablero
            </label>
            <label className="switch">
              <input
                type="checkbox"
                id="sendToBoard"
                checked={!!targetWorkspace}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Si se activa, establecer el primer workspace disponible
                    setTargetWorkspace(allWorkspaces.length > 0 ? allWorkspaces[0].id : activeWorkspace);
                  } else {
                    // Si se desactiva, limpiar la selección
                    setTargetWorkspace('');
                  }
                }}
              />
              <span className="slider"></span>
            </label>
          </div>
          
          {targetWorkspace && (
            <div className="target-workspace-selector">
              <select
                id="targetWorkspace"
                value={targetWorkspace}
                onChange={(e) => setTargetWorkspace(e.target.value)}
                className="workspace-selector"
              >
                {allWorkspaces.map(workspace => (
                  <option key={workspace.id} value={workspace.id}>
                    {workspace.icon} {workspace.name}
                  </option>
                ))}
              </select>
              <small className="form-hint">
                Selecciona el tablero al que quieres enviar esta tarea
              </small>
            </div>
          )}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="assignee">Responsable</label>
        <div className="assignee-input-container">
          <div className="autocomplete-container">
            <input
              type="text"
              id="assignee"
              name="assignee"
              value={assigneeQuery}
              onChange={handleAssigneeQueryChange}
              onFocus={handleAssigneeFocus}
              placeholder="Buscar responsable por nombre o email"
              className="assignee-input"
              disabled={loadingUsers}
              ref={assigneeInputRef}
            />
            
            {showAssigneeSuggestions && assigneeSuggestions.length > 0 && (
              <>
                <div ref={assigneeSuggestionsRef} className="autocomplete-suggestions">
                  {assigneeSuggestions.map(user => {
                    const formattedName = formatUserName(user.email);
                    return (
                      <div
                        key={user.email}
                        className="suggestion-item"
                        onClick={() => handleSelectAssignee(user)}
                        title={`${formattedName} (${user.department || ''})`}
                      >
                        <span className="user-name">{formattedName}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mobile-overlay" onClick={() => setShowAssigneeSuggestions(false)}></div>
              </>
            )}
            
            {showAssigneeSuggestions && assigneeSuggestions.length === 0 && (
              <div className="autocomplete-suggestions">
                <div className="suggestion-item no-results">
                  No se encontraron usuarios
                </div>
              </div>
            )}
          </div>
          
          {/* Botón X para limpiar */}
          {assigneeQuery && (
            <button 
              type="button"
              className="clear-filter-btn" 
              onClick={clearAssigneeFilter}
              title="Limpiar asignación"
            >
              ✕
            </button>
          )}
        </div>
        {loadingUsers && <div className="mini-spinner"></div>}
      </div>

      <div className="form-group">
        <label>Archivos adjuntos</label>
        
        {/* Display already uploaded files */}
        {uploadedFiles.length > 0 && (
          <div className="uploaded-files">
            <h5>Archivos subidos:</h5>
            <div className="file-list">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="file-item">
                  <span className="file-icon">{getFileIcon(file)}</span>
                  <span className="file-name" title={file.name}>
                    {file.name.length > 20 ? file.name.substring(0, 17) + '...' : file.name}
                  </span>
                  <span className="file-size">({Math.round(file.size / 1024)} KB)</span>
                  <button 
                    type="button"
                    className="remove-file-btn"
                    onClick={() => removeUploadedFile(index)}
                    title="Eliminar archivo"
                  >
                    ✖
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* File selection controls */}
        <div className="file-upload-controls">
          <div className="file-select-area">
            <label className="file-input-label">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.bmp,.webp"
                multiple
                onChange={handleFileChange}
                className="file-input"
                disabled={isUploading}
              />
              <span className="file-button">📎 Adjuntar archivos</span>
            </label>
            
            {selectedFiles.length > 0 && (
              <div className="selected-files">
                <span className="files-count">
                  {selectedFiles.length} {selectedFiles.length === 1 ? 'archivo seleccionado' : 'archivos seleccionados'}
                </span>
                <div className="file-preview">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="file-preview-item">
                      <span className="file-preview-icon">{getFileIcon(file)}</span>
                      <span className="file-preview-name" title={file.name}>
                        {file.name.length > 15 ? file.name.substring(0, 12) + '...' : file.name}
                      </span>
                      <button 
                        type="button" 
                        className="remove-file-btn"
                        onClick={() => removeSelectedFile(index)}
                        title="Eliminar selección"
                      >
                        ✖
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {isUploading && (
          <div className="upload-progress-container">
            <div 
              className="upload-progress-bar" 
              style={{width: `${uploadProgress}%`}}
            ></div>
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="urls">URLs</label>
        <div className="url-input-container">
          <div className="url-input-row">
            <input
              id="new-url"
              name="new-url"
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="Añadir URL relacionada con la tarea"
              className="url-input"
            />
            <button 
              type="button" 
              className="add-url-btn"
              onClick={handleAddUrl}
            >
              Añadir
            </button>
          </div>
          
          {formData.urls.length > 0 && (
            <div className="urls-list">
              {formData.urls.map((url, index) => (
                <div key={index} className="url-item">
                  <span className="url-icon">🔗</span>
                  <span className="url-text" title={url}>
                    {url}
                  </span>
                  <button 
                    type="button"
                    className="remove-url-btn"
                    onClick={() => handleRemoveUrl(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <small className="form-hint">Ingresa URLs relacionadas con esta tarea</small>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="tags">Etiquetas</label>
        
        {/* Mostrar etiquetas seleccionadas */}
        {formData.tags.length > 0 && (
          <div className="selected-tags">
            {formData.tags.map((tag, index) => (
              <div key={index} className="selected-tag">
                <span className="tag-text">{tag}</span>
                <button 
                  type="button"
                  className="remove-tag-btn"
                  onClick={() => handleRemoveTag(tag)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Input de etiquetas con autocompletado */}
        <div className="tag-input-container">
          <div className="autocomplete-container">
            <input
              type="text"
              id="tags"
              value={tagQuery}
              onChange={handleTagQueryChange}
              onKeyPress={handleTagKeyPress}
              onFocus={handleTagFocus}
              placeholder="Buscar etiquetas o escribir nueva etiqueta"
              className="tag-input"
              ref={tagInputRef}
            />
            
            {showTagSuggestions && tagSuggestions.length > 0 && (
              <>
                <div ref={tagSuggestionsRef} className="autocomplete-suggestions">
                  {tagSuggestions.map((tag, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onClick={() => handleSelectTag(tag)}
                    >
                      {tag}
                    </div>
                  ))}
                </div>
                <div className="mobile-overlay" onClick={() => setShowTagSuggestions(false)}></div>
              </>
            )}
          </div>
          
          {/* Botón X para limpiar */}
          {tagQuery && (
            <button 
              type="button"
              className="clear-filter-btn" 
              onClick={clearTagQuery}
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
        
        <small className="form-hint">Escribe para buscar etiquetas existentes o presiona Enter para crear una nueva</small>
      </div>

      <div className="form-group">
        <label htmlFor="endDate">Fecha de Vencimiento (Opcional)</label>
        <input
          id="endDate"
          name="endDate"
          type="datetime-local"
          value={formData.endDate || ''}
          onChange={handleChange}
          placeholder="Selecciona una fecha de vencimiento si lo deseas"
        />
      </div>



      <button type="submit" className="submit-button" disabled={isSubmitting}>
        {isSubmitting ? 'Guardando...' : (taskToEdit ? 'Guardar Tarea' : 'Crear Tarea')}
      </button>
    </form>
  );
}

export default TaskForm; 